<template>
  <article
    class="CardAddOn"
    :class="[
      { 'is-selected': selected },
      { 'is-disabled': disabled },
      { 'CardAddOn--compact': compact },
    ]"
  >
    <!-- Image Section -->
    <div v-if="imageUrl" class="CardAddOn-image">
      <slot name="image">
        <img
          :src="imageUrl"
          alt=""
          class="CardAddOn-imageElement"
          loading="lazy"
        >
      </slot>
    </div>

    <!-- Content Section -->
    <div class="CardAddOn-content">
      <!-- Details Section -->
      <div class="CardAddOn-details">
        <!-- Title + Tag Container -->
        <div class="CardAddOn-titleContainer">
          <slot name="title">
            <h4 class="CardAddOn-title">
              {{ title }}
            </h4>
          </slot>

          <slot name="tag">
            <Tag
              v-if="tag"
              size="sm"
              type="highlight"
              variant="filled"
            >
              {{ tag }}
            </Tag>
          </slot>
        </div>

        <!-- Description -->
        <slot name="description">
          <p v-if="description" class="CardAddOn-description">
            {{ description }}
          </p>
        </slot>
      </div>

      <!-- Pricing Section -->
      <div class="CardAddOn-pricing">
        <div class="CardAddOn-priceDisplay">
          <div class="CardAddOn-price">
            <slot name="price">
              <span class="CardAddOn-priceGroup">
                <s v-if="originalPrice" class="CardAddOn-originalPrice">{{ originalPrice }}</s>
                <span v-if="price" class="CardAddOn-priceAmount">{{ price }}</span>
              </span>
              <span v-if="frequency" class="CardAddOn-priceFrequency">{{ frequency }}</span>
              <span v-if="priceDisclaimer" class="CardAddOn-priceDisclaimer">{{ priceDisclaimer }}</span>
            </slot>
          </div>

          <Tag
            v-if="promoTagLabel"
            type="warning"
            variant="filled"
            size="xs"
            leftIcon="brightness-percent"
            class="CardAddOn-promoTag"
          >
            {{ promoTagLabel }}
          </Tag>

          <slot name="CardAddOn-secondaryText">
            <span v-if="secondaryText" class="CardAddOn-secondaryText">{{ secondaryText }}</span>
          </slot>
        </div>

        <div class="CardAddOn-actions">
          <div v-if="detailedPrice" class="CardAddOn-priceBreakdown">
            <AddonPriceBreakdown
              :outboundPrice="detailedPrice.outboundPrice"
              :inboundPrice="detailedPrice.inboundPrice"
            />
          </div>

          <slot name="actions">
            <ActionButton
              size="sm"
              :variant="selected ? 'primary' : 'secondary'"
              :disabled="disabled"
              :aria-label="buttonAriaLabel"
              :leftIcon="buttonIcon"
              data-testid="addon-card-select-action"
              @click.stop="toggleAddon"
            >
              {{ buttonLabel }}
            </ActionButton>
          </slot>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import AddonPriceBreakdown from './AddonPriceBreakdown.vue';
import type { CardAddOnProps, CardAddOnEmits } from './CardAddOn.model';

const props = withDefaults(defineProps<CardAddOnProps>(), {
  disabled: false,
  compact: false,
});

const emit = defineEmits<CardAddOnEmits>();

const selected = defineModel<boolean>('selected', { default: false });

const buttonLabel = computed(() => (selected.value ? props.addedButtonLabel : props.addButtonLabel));

const buttonAriaLabel = computed(() => `${buttonLabel.value} ${props.title}`);

const buttonIcon = computed(() => {
  return selected.value ? 'check' : '';
});

function toggleAddon(): void {
  if (props.disabled) return;

  const isSelected = !selected.value;
  selected.value = isSelected;
  emit('change', props.addonId, isSelected);
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui{
  .CardAddOn {
    display: flex;
    background-color: vars.$organisms-card-addon-bg-color;
    outline: vars.$organisms-card-addon-enabled-border-stroke solid vars.$organisms-card-addon-enabled-border-color;
    border-radius: vars.$organisms-card-addon-lg-md-border-radius;
    overflow: hidden;

    @include responsive.mobile {
      flex-direction: column;
      border-radius: vars.$organisms-card-addon-sm-border-radius;
    }

    &.is-selected {
      outline-color: vars.$organisms-card-addon-selected-border-color;
      outline-width: vars.$organisms-card-addon-selected-border-stroke;
    }

    &.is-disabled {
      cursor: not-allowed;
      outline-color: vars.$organisms-card-addon-included-border-color;
      outline-width: vars.$organisms-card-addon-included-border-stroke;
    }

    &-image {
      width: 120px;
      overflow: hidden;

      @include responsive.mobile {
        width: 100%;
        height: 96px;
      }
    }

    &-imageElement {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      object-fit: cover;
    }

    // Content Section
    &-content {
      flex: 1;
      padding: vars.$organisms-card-addon-lg-md-padding-top-bottom vars.$organisms-card-addon-lg-md-padding-left-right;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-4;

      @include responsive.mobile {
        padding: vars.$organisms-card-addon-sm-padding-top-bottom vars.$organisms-card-addon-sm-padding-left-right;
      }
    }

    // Details Section
    &-details {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;
    }

    &-titleContainer {
      display: flex;
      gap: vars.$dimensions-d-5;
      justify-content: space-between;
      align-items: center;
    }

    &-title {
      margin: 0;
      color: vars.$organisms-card-addon-title-color;
      @include text.heading('h4');
      flex: 1;
    }

    &-description {
      margin: 0;
      color: vars.$organisms-card-addon-text-color;
      @include text.text('sm');
    }

    // Pricing Section
    &-pricing {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: vars.$dimensions-d-4;

      @include responsive.mobile {
        flex-direction: column;
        align-items: stretch;
      }
    }

    &-priceDisplay {
      display: flex;
      flex-direction: column;
      row-gap: vars.$dimensions-d-2;
    }

    &-price {
      display: grid;
      grid-template-areas:
        'price frequency'
        'priceDisclaimer priceDisclaimer';
      grid-template-columns: auto 1fr;
      align-items: baseline;
      gap: vars.$dimensions-d-1;
    }

    &-priceGroup {
      grid-area: price;
      display: flex;
      align-items: baseline;
      gap: vars.$dimensions-d-2;
    }

    &-originalPrice {
      color: vars.$organisms-card-add-onpricing-secondary-text-color;
      @include text.text('sm');
      margin: 0;
    }

    &-priceAmount {
      color: vars.$organisms-card-add-onpricing-price-color;
      @include text.heading('h4');
      margin: 0;
    }

    &-priceFrequency {
      grid-area: frequency;
      color: vars.$organisms-card-add-onpricing-segment-text-color;
      @include text.text('sm');
      margin: 0;
    }

    &-priceDisclaimer {
      grid-area: priceDisclaimer;
      color: vars.$organisms-card-add-onpricing-secondary-text-color;
      @include text.text('xs');
    }

    &-secondaryText {
      color: vars.$organisms-card-add-onpricing-secondary-text-color;
      @include text.text('xs');
    }

    &-actions {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      align-items: flex-end;

      @include responsive.mobile {
        align-items: stretch;
      }
    }
  }
  .CardAddOn--compact {
    .CardAddOn {
      &-pricing {
        flex-direction: column;
        align-items: flex-start;
      }

      &-actions {
        width: 100%;
      }

      &-priceBreakdown {
        width: 100%;
      }
    }
  }
}
</style>